<template>
  <div class="card p-3">
    <v-stepper
      v-if="vuetifyWidget === 'stepper'"
      v-model="flowStep"
    >
      <v-stepper-header>
        <div class="d-flex align-center">
          <h4 class="ml-4">{{ getCurrentStepHeader }}</h4>
        </div>
        <v-spacer></v-spacer>
        <template v-for="(value, key, index) in showedSteps">
          <v-stepper-step
            :key="`${key}-step`"
            :step="index+1"
            :complete="complete(index)"
          >
          </v-stepper-step>
          <v-divider
              :key="`${key}-divider`"
              v-if="showedStepsCount !== index + 1">
          </v-divider>
        </template>
      </v-stepper-header>
      <v-stepper-items>
        <v-stepper-content
          v-for="(value, key, index) in showedSteps"
          v-bind:class="(flowStep === index + 1) ? 'flow-stepper-content' : ''"
          :key="`${key}-content`"
          :step="index+1"
        >
          <flow-step
            :ref="`step-${index+1}`"
            :schema="value"
            :flow-name="schema.name"
            :step-name="key"
            auto-continue
            :flatten-model="flattenModel"
            v-model="model[key]"
            @nextStep="onNextStep"
            @validationChanged="updateValidState"
          >
          </flow-step>
          <div>
            <v-btn
              v-show="!isFirstStep"
              data-test="buttonPrevStep"
              text
              @click="toPrevStep"
            >
              {{ $t('Back') }}
            </v-btn>
            <v-btn
              v-show="!isLastStep"
              :disabled="continueButtonDisabled"
              color="primary"
              data-test="buttonNextStep"
              @click="toNextStep"
            >
              {{ $t('Continue') }}
            </v-btn>
            <v-btn
              v-show="showSubmitButton"
              class="btn-submit"
              data-test="buttonSubmitFlow"
              @click="submit"
            >
              {{ $t('Submit') }}
            </v-btn>
          </div>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
    <v-expansion-panels
      v-else-if="vuetifyWidget === 'expansion'"
      v-model="flowStep"
      accordion
      flat
    >
      <slot name="steps-before"></slot>
      <v-expansion-panel
        @change="onChangeFlowStep"
        v-for="(stepSchema, stepKey, stepIndex) in showedSteps"
        v-bind:disabled="flowControl && disabledStep(stepIndex)"
        :key="`step-${stepIndex}`"
      >
        <v-divider
          class="mt-3 mb-4"
          v-if="showStepHeaderDividers"
        ></v-divider>
        <v-expansion-panel-header>
          <h2>
            <span class="expansion-panel-header-number">
              {{ stepIndex + shiftIndex + 1 }}.
            </span>
            <span class="expansion-panel-header-title">
              {{ stepSchema.title }}
            </span>
            <v-btn
              v-if="stepSchema.addRemoveStepButton"
              class="ml-5"
              color="#fafafa"
              depressed
              :data-test="`buttonRemoveStep-${stepKey}`"
              @click.stop="removeStep(stepKey)"
            >
              <v-icon class="mr-2">mdi-delete-forever-outline</v-icon>
              <span class="text-decoration-underline">
                {{ stepSchema.removeStepButtonText }}
              </span>
            </v-btn>
          </h2>
        </v-expansion-panel-header>
        <v-expansion-panel-content eager>
          <v-divider
            class="mt-4 mb-6"
            v-if="showStepHeaderDividers"
          ></v-divider>
          <p
            class="step-description"
            v-if="stepSchema.description"
          >
            {{ stepSchema.description }}
          </p>
          <flow-step
            v-if="hasQuestions(stepSchema)"
            :flatten-model="flattenModel"
            :ref="`step-${stepIndex}`"
            :schema="stepSchema"
            :flow-name="schema.name"
            :step-name="stepKey"
            :auto-continue="autoContinue"
            v-model="model[stepKey]"
            @nextStep="onNextStep"
            @validationChanged="updateValidState"
          >
          </flow-step>
          <div v-if="showButtons" class="mb-2">
            <v-btn
              v-show="!isFirstStep"
              text
              class="mr-2 btn-back"
              data-test="buttonBackStep"
              @click="toPrevStep"
            >
              {{ $t('Back') }}
            </v-btn>
            <v-btn
              v-show="!isLastStep"
              :disabled="continueButtonDisabled"
              color="primary"
              class="btn-continue"
              data-test="buttonContinueStep"
              @click="toNextStep"
            >
              {{ $t('Continue') }}
            </v-btn>
            <v-btn
              v-show="showSubmitButton"
              :disabled="continueButtonDisabled"
              class="btn-submit white--text"
              color="accent"
              text
              x-large
              tile
              data-test="buttonSubmitFlow"
              @click="submit"
            >
              {{ $t('Submit') }}
            </v-btn>
          </div>
          <div v-else>
            <v-btn
              v-show="showSubmitButton"
              :disabled="continueButtonDisabled"
              class="mb-2 btn-submit white--text"
              color="accent"
              x-large
              text
              tile
              data-test="buttonSubmitFlow"
              @click="submit"
            >
              {{ $t('Submit') }}
            </v-btn>
          </div>
        </v-expansion-panel-content>
        <v-divider
          v-if="stepIndex + 1 < showedStepsCount && !showStepHeaderDividers"
          class="mx-2"
        />
      </v-expansion-panel>
      <slot name="steps-after"></slot>
    </v-expansion-panels>
    <div v-if="!steps">
      <h5>Please fix you JSON scheme</h5>
    </div>
  </div>

</template>

<script>
  import FlowStep from "./FlowStep";
  import {_} from "vue-underscore";
  import {ComponentWithModelMixin, FlowGeneratorMixin, StepperValidationMixin} from "../../mixins/FlowGenerator";
  import {FlowGeneratorBus} from "../../plugins/bus"

  export default {
    name: "FlowStepper",
    components: {FlowStep},
    mixins: [FlowGeneratorMixin, ComponentWithModelMixin, StepperValidationMixin],
    computed: {
      //  To have ability to show/hide steps with logic based on flow answers
      //  we are using this computed property which expand raw `schema.steps`
      //  with `show` boolean attribute, also `index` to manage other UI logic
      steps() {
        let index = 0;
        const showStep = this.showStep;
        if (!_.isEmpty(this.schema.steps)) {
          // append `show` property to hide/show steps
          return _.mapObject(this.schema.steps, function (val, key) {
            index += 1;
            return {...val, show: showStep(key), index: index}
          })
        }
        return {}
      },
      //  returns only steps that have `show` attribute equals true
      showedSteps() {
        return _.pick(this.steps, value => value.show)
      },
      showedStepsCount() {
        return _.keys(this.showedSteps).length
      },
      stepsCount() {
        return this.showedStepsCount + this.stepsBeforeCount
      },
      isFirstStep() {
        return this.flowStep === this.getFirstStepIndex()
      },
      isLastStep() {
        const currentStep = this.flowStep + 1 - this.getFirstStepIndex()
        return this.stepsCount === currentStep
      },
      shiftIndex() {
        return this.stepsBeforeCount
      },
      stepRef() {
        return this.flowStep !== undefined && `step-${this.flowStep-this.shiftIndex}`;
      },
      getCurrentStepHeader() {
        const currentStepSchema = _.values(this.showedSteps)[this.flowStep-1]
        return currentStepSchema && currentStepSchema.title
      },
      // Our model have steps names as first-level keys,
      // but usually to implement some logic based on answers we wanna to work with
      // {[question id]: [answer value], ...}
      // and this property returns what we need.
      flattenModel() {
        return _.extend({}, ..._.values(this.model))
      },
      // Other customization stuff from schema
      showButtons() {
        return this.schema.showButtons || false
      },
      flowControl() {
        return this.schema.flowControl || false
      },
      switchControl() {
        return this.schema.switchControl || false
      },
      showStepHeaderDividers() {
        return this.schema.showStepHeaderDividers || false
      },
      autoContinue()  {
        return this.schema.autoContinue || false
      },
      hideSubmitButton() {
        return this.schema.hideSubmitButton || false
      },
      showSubmitButton() {
        return !this.hideSubmitButton && this.isLastStep
      },
      autoSubmit() {
        return this.schema.autoSubmit || false
      },
      vuetifyWidget() {
        return this.schema.vuetifyWidget || 'expansion'
      }
    },
    props: {
      schema: {
        type: Object,
        required: true
      },
      stepsBeforeCount: {
        type: Number,
        default: 0
      }
    },
    async mounted() {
      await this.uiWorkaround()
      this.loadModel()
      this.flowStep = this.getFirstStepIndex()
    },
    data() {
      return {
        // store index of active step
        flowStep: 0,
        model: {},
        continueButtonDisabled: true,
      }
    },
    methods: {
      // helpers for step UI
      editable(index) {
        return this.complete(index)
      },
      complete(index) {
        return index + 1 < this.flowStep
      },
      async uiWorkaround() {
        if (this.vuetifyWidget === 'stepper') return
        // workaround after route change
        await setTimeout(() => {
          this.flowStep = -1
        }, 150);
        await setTimeout(() => {
          this.flowStep = 0
        }, 200);
      },
      async onNextStep() {
        const isCurrentStepValid = await this.validateStep();
        if (!this.autoContinue) return
        await this.$nextTick()
        await this.toNextStep(isCurrentStepValid)
      },
      // action to activate next flow step
      async toNextStep(isCurrentStepValid = true) {
        if (this.isLastStep && this.autoSubmit) {
          this.submit()
        } else if (isCurrentStepValid === undefined || isCurrentStepValid) {
          this.flowStep = this.getNextStep()
          this.continueButtonDisabled = !await this.validateStep(true)
        }
      },
      // action to activate prev flow step
      async toPrevStep() {
        this.flowStep = this.getPrevStep()
        this.continueButtonDisabled = !await this.validateStep(true)
      },
      getPrevStep() {
        return this.flowStep - 1
      },
      getNextStep() {
        return this.flowStep + 1
      },
      getFirstStepIndex() {
        return this.vuetifyWidget === 'stepper' ? 1 : 0
      },
      showStep(stepName) {
        const hide = this.schema.steps[stepName].hide
        const when = this.schema.steps[stepName].when
        return !hide && (!when || this.isMatchWhen(this.flattenModel, when))
      },
      removeStep(stepKey) {
        FlowGeneratorBus.$emit('remove-step', stepKey)
      },
      hasQuestions(stepSchema) {
        return stepSchema.questions && stepSchema.questions.length
      },
      async validateStep(silent = false) {
        if (!this.stepRef) return;
        const step = this.flowStepRef(this.stepRef)
        if (step === undefined) return;
        return await step.validate(silent);
      },
      getStepKey(index) {
        // get current step by index
        // and return Ref to flowStepper component
        const step = _.pairs(
          _.pick(this.showedSteps, step => step.index === index)
        )
        // sa pairs return us ["key", "value"] when transform object
        if (step.length > 0) return step[0][0]
      },
      flowStepRef(ref) {
        return this.$refs[ref] && this.$refs[ref][0];
      },
      flowStepIsValid(index) {
        const step = this.flowStepRef(`step-${index}`)
        return !!step && step.valid
      },
      updateValidState(stepKey, isValid) {
        this.validationSteps[stepKey] = isValid
        this.continueButtonDisabled = !isValid
      },
      async onChangeFlowStep() {
        const fromStep = this.flowStep
        await this.validateStep(true)
        await this.$nextTick()
        const toStep = this.flowStep
        // prevent control when first step is open or last close
        if (toStep === undefined || fromStep === undefined || !this.switchControl || fromStep < this.shiftIndex) {
          return
        } else {
          // check we are switching to step after and return to fromStep when it's invalid
          if (!this.flowStepIsValid(fromStep - this.shiftIndex) && toStep > fromStep) {
            this.flowStep = fromStep
            await this.$nextTick()
            await this.validateStep()
          }
        }
      }
    }
  }
</script>

<style lang="scss">
  .v-stepper__content.flow-stepper-content {
    .v-stepper__wrapper {
      height: auto !important;
      padding-top: 8px;
    }
  }
</style>
